import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';

import { fetchCustomerDetail } from '#/api/crmMgmt/customer/fetchDetail';
import { fetchProductDetail } from '#/api/crmMgmt/product/fetchDetail';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import ProductBlock from '#/modules/crmMgmt/product/components/ProductBlock';
import { getProductApiErrorMessage } from '#/modules/crmMgmt/product/helper/apiError';
import PageShell from '#/modules/infra/layout/PageShell';
import {
  customerDetailQueryKey,
  productDetailQueryKey,
} from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

function formatCustomerLabel(customer) {
  if (!customer) {
    return '';
  }
  return [customer.code, customer.name].filter(Boolean).join(' - ');
}

const ProductDetailPage = () => {
  const { id } = useParams();

  const detailQuery = useQuery({
    queryKey: productDetailQueryKey(id),
    queryFn: () => fetchProductDetail(id),
    enabled: Boolean(id),
  });

  const customerId = detailQuery.data?.data?.customerId;

  const customerQuery = useQuery({
    queryKey: customerDetailQueryKey(customerId),
    queryFn: () => fetchCustomerDetail(customerId),
    enabled: Boolean(customerId),
  });

  useEffect(() => {
    if (!detailQuery.isError) {
      return;
    }
    toast.error(getProductApiErrorMessage(detailQuery.error, '載入產品失敗'));
  }, [detailQuery.isError, detailQuery.error]);

  const detail = detailQuery.data?.data;
  const isLoading = detailQuery.isLoading;
  const isError = detailQuery.isError;
  const customerLabel = useMemo(
    () => formatCustomerLabel(customerQuery.data?.data),
    [customerQuery.data?.data],
  );

  return (
    <PageShell variant="detail">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-4xl space-y-4">
          <div
            className={cn(
              'sticky top-0 z-20 -mx-1 flex flex-wrap items-center gap-2',
              'border-b border-slate-200 bg-canvas px-1 pb-3 pt-1',
            )}
          >
            <h1 className="page_title mb-0!">
              <span className="border-l-5 border-primary-400 pl-2.5">產品詳情</span>
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={ROUTES.product}>返回列表</Link>
            </Button>
          </div>

          {isLoading ? (
            <p className="text-sm text-slate-500">載入中…</p>
          ) : null}

          {isError ? (
            <p className="text-sm text-destructive">
              {getProductApiErrorMessage(detailQuery.error, '載入產品失敗')}
            </p>
          ) : null}

          {detail ? (
            <ProductBlock
              detail={detail}
              showActions
              customerLabel={customerLabel}
              onRefetch={() => detailQuery.refetch()}
            />
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default ProductDetailPage;
